import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

const noopStorage = {
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined,
};

function getSafeStorage() {
  if (typeof window === 'undefined') return noopStorage;
  try {
    return window.localStorage;
  } catch {
    return noopStorage;
  }
}

export interface FavoriteProduct {
  id: string;
  name: string;
  slug: string;
  category: string;
  image?: string;
}

interface FavoritesStore {
  items: FavoriteProduct[];
  addFavorite: (product: FavoriteProduct) => void;
  removeFavorite: (id: string) => void;
  toggleFavorite: (product: FavoriteProduct) => void;
  isFavorite: (id: string) => boolean;
  getTotalFavorites: () => number;
}

export const useFavoritesStore = create<FavoritesStore>()(
  persist(
    (set, get) => ({
      items: [],

      addFavorite: (product) => {
        set((state) => {
          if (state.items.some((item) => item.id === product.id)) {
            return state;
          }

          return { items: [...state.items, product] };
        });
      },

      removeFavorite: (id) => {
        set((state) => ({
          items: state.items.filter((item) => item.id !== id),
        }));
      },

      toggleFavorite: (product) => {
        if (get().isFavorite(product.id)) {
          get().removeFavorite(product.id);
          return;
        }

        get().addFavorite(product);
      },

      isFavorite: (id) => {
        return get().items.some((item) => item.id === id);
      },

      getTotalFavorites: () => {
        return get().items.length;
      },
    }),
    {
      name: 'favorites-storage',
      storage: createJSONStorage(getSafeStorage),
    }
  )
);
